import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ConversationChannelScope,
	type LastAgentMessageStatus,
	type MessageType,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	type AgentMetadata,
	type AgentVersion,
	CHAT_STORE_STATUS,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createSafeAction, type SafeAction } from './createSafeAction';
import { initializeConversationData } from './initializeConversationData';
import { resolveConversationSeedAgent } from './resolveConversationSeedAgent';
import { setSelectedConversation } from './setSelectedConversation';
import type { ChatStoreAction } from './types';

type CreateConversationOptimisticallyOptions = {
	name?: string;
	sourceId?: string;
	id?: string;
	timeCreated?: string;
	lastMessageTimestamp?: string;
	sourceAgentId?: string;
	status?: CHAT_STORE_STATUS;
	lastMessageType?: MessageType[];
	lastAgentMessageStatus?: LastAgentMessageStatus;
	agent?: AgentMetadata;
	agentVersion?: AgentVersion;
	rovoSpaceId?: string;
	scopes?: ConversationChannelScope[];
};

export const createConversationOptimistically: SafeAction<
	[CreateConversationOptimisticallyOptions]
> = createSafeAction(
	StoreActionLoggableName.createConversationOptimistically,
	(options: CreateConversationOptimisticallyOptions): ChatStoreAction =>
		async ({ dispatch }, { setMessages }) => {
			const conversationId = options?.id ?? EMPTY_CONVERSATION_ID;
			const time = new Date().toISOString();
			// Seed `conversation.agent` from the known optimistic conversation identity.
			const agentForOptimisticConversation = resolveConversationSeedAgent({
				backendAgent: options?.agent,
				agentId: options?.sourceAgentId,
				legacyAgent: options?.agent,
			});
			// Under the selected-agent gate the header derives the displayed agent
			// solely from `conversation.agent`. When this optimistic creation re-uses an
			// existing conversation slot (e.g. EMPTY_CONVERSATION_ID) the data is
			// shallow-merged, so omitting the `agent` key would leave a previously-seeded
			// specialist agent in place. Selecting "Chat with Rovo" (no agent id) must
			// therefore explicitly clear `conversation.agent` back to the default Rovo
			// agent rather than silently preserving the stale one.
			const optimisticConversationAgentData = agentForOptimisticConversation
				? { agent: agentForOptimisticConversation }
				: fg('rovo_chat_agent_selection')
					? { agent: undefined } // explicit clear so "Chat with Rovo" resets a stale specialist
					: {};

			dispatch(
				initializeConversationData({
					conversationId,
					data: {
						id: conversationId,
						lastMessageTimestamp: options.lastMessageTimestamp ?? time,
						name: options.name ?? generateConversationName(),
						sourceAgentId: options.sourceAgentId,
						sourceId: options.sourceId ?? undefined,
						status: options?.status ?? CHAT_STORE_STATUS.idle,
						timeCreated: options?.timeCreated ?? time,
						traces: [],
						messages: [],
						lastMessageType: options?.lastMessageType ?? [],
						lastAgentMessageStatus: options?.lastAgentMessageStatus,
						// Explicitly set rovoSpaceId (including undefined) so that re-initialising
						// EMPTY_CONVERSATION_ID without a space clears any stale rovoSpaceId left
						// from a previous space-scoped conversation.
						rovoSpaceId: options?.rovoSpaceId,
						...optimisticConversationAgentData,
						...(options?.agentVersion ? { agentVersion: options.agentVersion } : {}),
						...(options?.scopes ? { scopes: options.scopes } : {}),
					},
				}),
			);

			setMessages(conversationId, []);

			dispatch(
				setSelectedConversation({
					conversationId,
				}),
			);
		},
);
